import React, { useState, useRef } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useConfigContext, useLoadContext } from "shared/src/libs/contextLib/contextLib"
import { Button } from "shared/src/components/shadcn/components/ui/button"
import { Input } from "shared/src/components/shadcn/components/ui/input"
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "shared/src/components/shadcn/components/ui/card"
import { Field, FieldLabel, FieldContent, FieldError } from "shared/src/components/shadcn/components/ui/field"
import { Alert, AlertDescription } from "shared/src/components/shadcn/components/ui/alert"
import {
	Form,
	FormField,
	FormItem,
	FormLabel,
	FormControl,
	FormMessage,
} from "shared/src/components/shadcn/components/ui/form"
import { PasswordInput } from "shared/src/components/shadcn/components/atoms/inputs/PasswordInput"
import { AutoSaveInput, type AutoSaveStatus } from "shared/src/components/shadcn/components/atoms/inputs/AutoSaveInput"
import { Spinner } from "shared/src/components/shadcn/components/ui/spinner"
import { onError } from "shared/src/libs/errorLib/errorLib"
import { validatePassword, validateUserName } from "shared/src/libs/validationLib/validationLib"
import { patchUser } from "shared/src/libs/apiLib/apiLib"
import { Check } from "lucide-react"
import { Auth } from "@aws-amplify/auth"

// Password form schema
const passwordSchema = z
	.object({
		oldPassword: z.string().min(1, "Current password is required"),
		newPassword: z
			.string()
			.min(8, "Minimum 8 characters")
			.refine((val) => validatePassword(val), {
				message: "Must contain a number, lowercase, uppercase, special character, and minimum 8 characters",
			}),
		confirmPassword: z.string(),
	})
	.refine((data) => data.newPassword === data.confirmPassword, {
		message: "Passwords don't match",
		path: ["confirmPassword"],
	})

type PasswordFormValues = z.infer<typeof passwordSchema>

interface FormRowProps {
	label: string
	children: React.ReactNode
}

const FormRow = ({ label, children }: FormRowProps) => (
	<FormItem className="grid gap-2 sm:grid-cols-[160px_1fr] sm:items-start">
		<FormLabel className="">{label}</FormLabel>
		<div className="max-w-md space-y-1.5">
			{children}
			<FormMessage />
		</div>
	</FormItem>
)

export function AccountTab() {
	const { user, setUser, company, setCompany } = useLoadContext()
	const config = useConfigContext()

	// Profile state (auto-save on blur)
	const [name, setName] = useState(user.name)
	const [nameStatus, setNameStatus] = useState<AutoSaveStatus>("idle")
	const originalName = useRef(user.name)

	// Password form
	const form = useForm<PasswordFormValues>({
		resolver: zodResolver(passwordSchema),
		mode: "onChange",
		defaultValues: {
			oldPassword: "",
			newPassword: "",
			confirmPassword: "",
		},
	})

	const [submitStatus, setSubmitStatus] = useState<"idle" | "submitting" | "success">("idle")

	// Handle name auto-save
	async function handleNameSave(trimmedName: string): Promise<void> {
		if (trimmedName === originalName.current) return

		await patchUser({
			userId: user.id,
			email: user.email,
			name: trimmedName,
			clientId: company.id,
			clientName: company.name,
		})
		setUser({ ...user, name: trimmedName })
		originalName.current = trimmedName
		const newCompany = {
			...company,
			team: {
				...company.team,
				[user.id]: {
					user_role: user.role,
					name: trimmedName,
					email: user.email,
				},
			},
		}
		setCompany(newCompany)
	}

	// Validate name
	function validateName(value: string): boolean {
		return value.trim() !== "" && validateUserName(value)
	}

	// Handle password form submission
	async function onSubmit(values: PasswordFormValues) {
		setSubmitStatus("submitting")
		try {
			const response = await Auth.currentAuthenticatedUser()
			await Auth.changePassword(response, values.oldPassword, values.newPassword)
			setSubmitStatus("success")
			form.reset()
			setTimeout(() => {
				setSubmitStatus("idle")
			}, 3000)
		} catch (e) {
			onError(e)
			setSubmitStatus("idle")
			form.setError("oldPassword", {
				type: "manual",
				message: "Current password is incorrect",
			})
		}
	}

	function openIdentityManagementPage() {
		window.open(`${config.evoConfig.ACCESS_IDENTITY_URL}/manage/changepassword`, "_blank")
	}

	return (
		<div className="space-y-6">
			{/* Profile Card - Auto-save */}
			<Card>
				<CardHeader>
					<CardTitle>Profile</CardTitle>
					<CardDescription>Your personal information. Name changes save automatically.</CardDescription>
				</CardHeader>
				<CardContent className="space-y-6">
					{/* Name field with auto-save */}
					<Field
						orientation="horizontal"
						className="grid gap-2 sm:grid-cols-[160px_1fr] sm:items-start"
						data-invalid={nameStatus === "error"}>
						<FieldLabel htmlFor="name">Name</FieldLabel>
						<FieldContent className="max-w-md space-y-1.5">
							<AutoSaveInput
								id="name"
								value={name}
								onChange={setName}
								onSave={handleNameSave}
								validate={validateName}
								status={nameStatus}
								onStatusChange={setNameStatus}
								placeholder="Your name"
								autoComplete="name"
							/>
							{nameStatus === "error" && <FieldError>Failed to save name. Please try again.</FieldError>}
						</FieldContent>
					</Field>

					{/* Email field - read only */}
					<Field orientation="horizontal" className="grid gap-2 sm:grid-cols-[160px_1fr] sm:items-start">
						<FieldLabel htmlFor="email">Email</FieldLabel>
						<FieldContent className="max-w-md">
							<Input id="email" value={user.email} disabled className="bg-muted" autoComplete="email" />
						</FieldContent>
					</Field>
				</CardContent>
			</Card>

			{/* Password Card - Form submission or SSO */}
			<Card>
				<CardHeader>
					<CardTitle>Change Password</CardTitle>
					<CardDescription>
						{user.ssoLogin
							? "Manage your account and password through your identity provider."
							: "Must contain a number, lowercase, uppercase, special character, and minimum 8 characters."}
					</CardDescription>
				</CardHeader>
				<CardContent>
					{user.ssoLogin ? (
						<div className="flex justify-center w-full">
							<Button size="lg" onClick={openIdentityManagementPage}>
								Manage Account
							</Button>
						</div>
					) : (
						<Form {...form}>
							<form
								onSubmit={(e) => {
									void form.handleSubmit(onSubmit)(e)
								}}
								className="space-y-6">
								{/* Hidden username field for accessibility */}
								<input type="text" name="username" autoComplete="username" value={user.email} readOnly hidden />

								<FormField
									control={form.control}
									name="oldPassword"
									render={({ field, fieldState }) => (
										<FormRow label="Current Password">
											<FormControl>
												<PasswordInput autoComplete="one-time-code" error={!!fieldState.error} {...field} />
											</FormControl>
										</FormRow>
									)}
								/>

								<FormField
									control={form.control}
									name="newPassword"
									render={({ field, fieldState }) => (
										<FormRow label="New Password">
											<FormControl>
												<PasswordInput autoComplete="new-password" error={!!fieldState.error} {...field} />
											</FormControl>
										</FormRow>
									)}
								/>

								<FormField
									control={form.control}
									name="confirmPassword"
									render={({ field, fieldState }) => (
										<FormRow label="Confirm Password">
											<FormControl>
												<PasswordInput autoComplete="new-password" error={!!fieldState.error} {...field} />
											</FormControl>
										</FormRow>
									)}
								/>

								{/* Success message */}
								{submitStatus === "success" && (
									<div className="sm:ml-[168px]">
										<Alert variant="success" className="w-fit">
											<Check className="h-4 w-4" />
											<AlertDescription>Password updated successfully</AlertDescription>
										</Alert>
									</div>
								)}

								{/* Submit button */}
								<div className="sm:ml-[168px]">
									<Button type="submit" disabled={submitStatus === "submitting"}>
										{submitStatus === "submitting" ? (
											<>
												<Spinner className="mr-2 size-4" />
												Updating...
											</>
										) : (
											"Update password"
										)}
									</Button>
								</div>
							</form>
						</Form>
					)}
				</CardContent>
			</Card>
		</div>
	)
}
